import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { colors } from '../styles/colors';
import { Download } from 'lucide-react';
import { useFillout } from '../hooks/useFillout';

const LiveReserveReleasePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.black }}>
      {/* Fillout popup is now managed globally via FilloutProvider */}

      {/* SEO & Open Graph Metadata */}
      <Helmet>
        <title>Live Reserve Update | Arizore</title>
        <meta name="description" content="Arizore confirms $50.1 million in live allocated reserve as AZG and AZS complete on-chain mint. Structured bullion representation designed for institutional collateral use." />
        <link rel="canonical" href="https://arizore.io/reserve-room/written/live-reserve" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Live Reserve Update" />
        <meta property="og:description" content="Arizore confirms $50.1 million in live allocated reserve as AZG and AZS complete on-chain mint. Structured bullion representation designed for institutional collateral use." />
        <meta property="og:image" content="https://arizore.io/arizore-pr-release-1-og.png" />
        <meta property="og:url" content="https://arizore.io/reserve-room/written/live-reserve" />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Live Reserve Update" />
        <meta name="twitter:description" content="Arizore confirms $50.1 million in live allocated reserve as AZG and AZS complete on-chain mint. Structured bullion representation designed for institutional collateral use." />
        <meta name="twitter:image" content="https://arizore.io/arizore-pr-release-1-og.png" />
      </Helmet>

      {/* Main Content */}
      <article className="pt-24 sm:pt-28 pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          
          {/* Breadcrumb */}
          <div 
            className={`mb-4 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span 
              className="text-xs sm:text-sm font-medium uppercase tracking-wider"
              style={{ color: colors.teal }}
            >
              Live Reserve
            </span>
          </div>

          {/* Page Title */}
          <h1 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white, transitionDelay: '50ms' }}
          >
            Arizore Confirms $50.1M Live Reserve as AZG and AZS Mint Completes On-Chain
          </h1>

          {/* Publish Date & Author */}
          <div 
            className={`mb-8 sm:mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <span 
              className="text-sm"
              style={{ color: colors.white, opacity: 0.5 }}
            >
              26 February 2026 · Arizore Team
            </span>
          </div>

          {/* Hero Image */}
          <div 
            className={`mb-8 sm:mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            <img 
              src="/arizore-pr-release-1.png" 
              alt="Arizore Live Reserve Update" 
              className="w-full rounded-lg"
            />
          </div>

          {/* Subheadline */}
          <p 
            className={`text-base sm:text-lg mb-10 sm:mb-12 pb-8 sm:pb-10 border-b transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ 
              color: colors.white, 
              opacity: 0.7, 
              borderColor: `${colors.white}15`,
              transitionDelay: '200ms',
              fontStyle: 'italic'
            }}
          >
            Verified Regulated Security Tokens Now Fully Issued, Visible, and Mirrored Through Live Reserve Infrastructure
          </p>

          {/* Article Content */}
          <div 
            className={`prose prose-lg max-w-none transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            {/* Opening */}
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              <strong style={{ color: colors.white }}>[Sydney, 22/02/2026].</strong> Arizore today confirms the successful on-chain minting and live reserve activation of its AZG and AZS tokens, representing a combined reserve value of $50,134,688, now fully visible via the Arizore website and mirrored through its live reserve data infrastructure.
            </p>

            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              This milestone marks the formal transition of Arizore's reserve architecture from structured design to operational reality.
            </p>

            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              All tokens have been minted, issued, and confirmed on-chain. Reserve values, token counts, and aggregate reserve totals are now publicly visible and programmatically accessible through the live API layer.
            </p>

            {/* Reserve Confirmation Section */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Reserve Confirmation
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1rem', lineHeight: '1.8' }}>
              The live reserve currently reflects the following:
            </p>

            {/* AZG Box */}
            <div 
              style={{ 
                backgroundColor: `${colors.white}05`, 
                border: `1px solid ${colors.white}15`,
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1rem'
              }}
            >
              <h3 style={{ color: '#F2D675', fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                AZG (Gold Reserve Layer)
              </h3>
              <ul style={{ color: colors.white, opacity: 0.85, marginLeft: '1.25rem', listStyleType: 'disc', lineHeight: '1.8' }}>
                <li>8,000 tokens minted and in circulation</li>
                <li>$5,000 per token</li>
                <li>Total reserve value: $40,000,000</li>
              </ul>
            </div>

            {/* AZS Box */}
            <div 
              style={{ 
                backgroundColor: `${colors.white}05`, 
                border: `1px solid ${colors.white}15`,
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}
            >
              <h3 style={{ color: '#EDEDED', fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                AZS (Silver Reserve Layer)
              </h3>
              <ul style={{ color: colors.white, opacity: 0.85, marginLeft: '1.25rem', listStyleType: 'disc', lineHeight: '1.8' }}>
                <li>130,400 tokens minted and in circulation</li>
                <li>$77.72 per token</li>
                <li>Total reserve value: $10,134,688</li>
              </ul>
            </div>

            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              <strong style={{ color: colors.white }}>Combined live reserve value: $50,134,688</strong>
            </p>

            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              All figures are mirrored directly from the live reserve system and reflect current on-chain issuance and reserve backing.
            </p>

            {/* A Reserve-First Architecture */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              A Reserve-First Architecture
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Arizore is structured around a three-layer architecture designed to separate governance, reserve management, and token issuance.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The model is verification-led and delivery-anchored. Tokens are issued exclusively against verified reserve value and exist within a structured framework designed to align with institutional standards of clarity, segregation, and transparency.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The reserve strengthens only through verified asset inclusion. No issuance occurs without corresponding reserve value.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              This structure is intended not to accelerate speculation, but to formalise recognition.
            </p>

            {/* Why This Matters */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Why This Matters
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Across global capital markets, significant real-world asset value remains structurally under-recognised within traditional financial systems.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Arizore's approach is designed to make verified assets legible to institutional capital without forcing premature liquidity events or ownership transfer.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              With the reserve now live and visible, Arizore moves from conceptual framework to operational infrastructure.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1rem', lineHeight: '1.8' }}>
              This milestone establishes:
            </p>
            <ul style={{ color: colors.white, opacity: 0.85, marginLeft: '1.25rem', marginBottom: '1.5rem', listStyleType: 'disc', lineHeight: '1.8' }}>
              <li>Confirmed on-chain issuance</li>
              <li>Live reserve visibility</li>
              <li>Transparent token-to-reserve alignment</li>
              <li>Programmatic data accessibility via API</li>
              <li>Structural separation between governance, reserve, and token layers</li>
            </ul>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The result is a reserve model designed for long-term credibility rather than short-term momentum.
            </p>

            {/* Statement */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Statement from George Boot, Chief Marketing Officer, Arizore
            </h2>
            <blockquote 
              style={{ 
                borderLeft: `3px solid ${colors.teal}`,
                paddingLeft: '1.5rem',
                marginLeft: 0,
                marginBottom: '1.5rem'
              }}
            >
              <p style={{ color: colors.white, opacity: 0.9, marginBottom: '1rem', lineHeight: '1.8', fontStyle: 'italic' }}>
                "Today's confirmation is not about token issuance alone. It is about infrastructure becoming visible.
              </p>
              <p style={{ color: colors.white, opacity: 0.9, marginBottom: '1rem', lineHeight: '1.8', fontStyle: 'italic' }}>
                The reserve is live. The tokens are minted. The values are transparent. The architecture is operational.
              </p>
              <p style={{ color: colors.white, opacity: 0.9, marginBottom: '1rem', lineHeight: '1.8', fontStyle: 'italic' }}>
                We have deliberately built this system around verification, separation, and discipline. The objective has never been speed. It's been structural clarity.
              </p>
              <p style={{ color: colors.white, opacity: 0.9, lineHeight: '1.8', fontStyle: 'italic' }}>
                This is a foundational step toward a more transparent reserve-first model for asset recognition. It is not the result of a marketing cycle. It signals the transition from architecture to active infrastructure."
              </p>
            </blockquote>

            {/* A Measured Path Forward */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              A Measured Path Forward
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Arizore's strategy remains eligibility-led and institutionally aligned. Growth will continue to be governed by verification standards, jurisdictional discipline, and reserve integrity.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The live reserve does not represent a forecast or ambition statement. It reflects confirmed issuance against verified value.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              With infrastructure now active and publicly visible, Arizore enters its next phase as an operational reserve system designed to support structured, asset-backed financial participation.
            </p>

            {/* About Arizore */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              About Arizore
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Arizore is a reserve-first infrastructure platform designed to enable the recognition and structured mobilisation of verified real-world assets. Through its layered architecture, Arizore separates governance, reserve management, and token issuance to align with institutional standards of transparency, custody, and accountability.
            </p>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              For more information, visit{' '}
              <a 
                href="https://www.arizore.io" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: colors.teal, textDecoration: 'underline' }}
              >
                www.arizore.io
              </a>
            </p>
          </div>

          {/* CTA Buttons */}
          <div 
            className={`mt-12 sm:mt-16 pt-8 sm:pt-10 border-t transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ borderColor: `${colors.white}15`, transitionDelay: '400ms' }}
          >
            {/* PDF Download Button */}
            <a
              href="/arizore-pr-live-reserve.pdf"
              download="Arizore-PR-Live-Reserve-Update.pdf"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start"
              style={{ 
                backgroundColor: `${colors.white}10`,
                color: colors.white,
                opacity: 0.9
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}20`;
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}10`;
                e.currentTarget.style.opacity = '0.9';
              }}
            >
              <Download className="w-4 h-4" />
              Download Full Release (PDF)
            </a>

            {/* Talk to the Team Button */}
            <button
              onClick={openFilloutPopup}
              data-testid="live-reserve-talk-to-team-btn"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start mt-4 sm:mt-0 sm:ml-4"
              style={{ 
                backgroundColor: 'transparent',
                color: colors.white,
                opacity: 0.7,
                border: `1px solid ${colors.white}30`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}10`;
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.opacity = '0.7';
              }}
            >
              Talk to the team
            </button>
          </div>

          {/* Disclaimer Block */}
          <div 
            className={`mt-12 sm:mt-16 pt-8 sm:pt-10 border-t transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ borderColor: `${colors.white}10`, transitionDelay: '500ms' }}
          >
            <p 
              className="text-xs leading-relaxed"
              style={{ color: colors.white, opacity: 0.4 }}
            >
              This release is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. Figures reflect live reserve data at the time of publication and are subject to change based on reserve activity.
            </p>
            <p 
              className="text-xs leading-relaxed mt-4"
              style={{ color: colors.white, opacity: 0.4 }}
            >
              AZG and AZS are reserve-backed digital instruments structured for use within defined institutional arrangements. They are not investment products, are not yield bearing, and are not intended for retail distribution or public trading.
            </p>
          </div>

        </div>
      </article>
    </div>
  );
};

export default LiveReserveReleasePage;
